'use client'

import { Plus24, X12 } from '@vinted/monochrome-icons'
import { Cell, Icon, Image, Spacer, Text } from '@vinted/web-ui'
import { keyBy } from 'lodash'
import { useIntl } from 'react-intl'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { HorizontalScrollArea } from '@marketplace-web/common-components/horizontal-scroll-area-ui'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import { BumpOrderItemModel } from '@marketplace-web/vas/vas-data'

import useBumpCheckoutContext from '../../../hooks/useBumpCheckoutContext'

type Props = {
  onAddButtonClick: () => void
  orderItems: Array<BumpOrderItemModel> | undefined
}

const ItemSelection = ({ onAddButtonClick, orderItems = [] }: Props) => {
  const { locale } = useIntl()
  const translate = useTranslate('bump_items')
  const translateA11y = useTranslate('a11y')

  const { selectedItems, setSelectedItems } = useBumpCheckoutContext()

  const handleAddButtonClick = () => {
    onAddButtonClick()
  }

  const handleRemoveButtonClick = (itemId: number) => () => {
    const updatedItems = selectedItems.filter(item => item.id !== itemId)

    setSelectedItems(updatedItems)
  }

  const renderItemPrice = (itemPrice: string, oldItemPrice: string, isOldPriceVisible: boolean) => {
    return (
      <>
        <Spacer />
        <Text
          as="span"
          width="parent"
          alignment="center"
          type="subtitle"
          theme="amplified"
          text={itemPrice}
          aria={{ 'aria-hidden': 'true' }}
        />
        {isOldPriceVisible && (
          <Text
            as="span"
            width="parent"
            alignment="center"
            type="caption"
            strikethrough
            text={oldItemPrice}
            aria={{ 'aria-hidden': 'true' }}
          />
        )}
      </>
    )
  }

  const renderItems = () => {
    const orderItemsByKey = keyBy(orderItems, 'itemId')

    return selectedItems.map(item => {
      const { id: itemId } = item

      const bumpOrderItem = orderItemsByKey[itemId]
      if (!bumpOrderItem) return null

      const itemTitle = bumpOrderItem.title
      const itemPhoto = bumpOrderItem.imageUrl

      const itemPrice = formatCurrencyAmount(bumpOrderItem.discountedPrice, locale)

      const discountedAmount = parseFloat(bumpOrderItem.discountedPrice.amount)
      const totalAmount = parseFloat(bumpOrderItem.totalPrice.amount)

      const isOldPriceVisible = totalAmount > discountedAmount
      const oldItemPrice = formatCurrencyAmount(bumpOrderItem.totalPrice, locale)

      const showRemoveButton = selectedItems.length !== 1

      let itemAriaLabel = `${itemTitle}. ${itemPrice}`
      if (isOldPriceVisible)
        itemAriaLabel = `${itemTitle}. ${translate('a11y.price', { price: itemPrice, original_price: oldItemPrice })}`

      return (
        <HorizontalScrollArea.Item key={itemId} ariaLabel={itemAriaLabel}>
          <div className="bump-items__content-overlay">
            <Image
              src={itemPhoto}
              size="x2-large"
              styling="rounded"
              alt=""
              ratio="small-portrait"
              testId={`bump-item-image-${itemId}`}
            />
            {showRemoveButton && (
              <button
                type="button"
                className="button-overlay"
                onClick={handleRemoveButtonClick(itemId)}
                data-testid={`bump-remove-item-button-${itemId}`}
                aria-label={translate('a11y.actions.remove_item')}
              >
                <Icon name={X12} color="greyscale-level-1" />
              </button>
            )}
          </div>
          {renderItemPrice(itemPrice, oldItemPrice, isOldPriceVisible)}
        </HorizontalScrollArea.Item>
      )
    })
  }

  const renderAddButton = () => {
    return (
      <div className="bump-items__add-button">
        <Cell
          styling="tight"
          theme="transparent"
          type="navigating"
          chevron={false}
          onClick={handleAddButtonClick}
          aria={{ 'aria-label': translate('a11y.actions.add_items') }}
          testId="bump-add-more-items-button"
        >
          <div className="bump-items__add-button-content">
            <Icon name={Plus24} color="greyscale-level-1" />
            <Spacer size="regular" />
            <Text
              as="span"
              type="caption"
              text={translate('selection_confirmation_modal.item_selection.add_more')}
              theme="amplified"
              alignment="center"
            />
          </div>
        </Cell>
      </div>
    )
  }

  if (!orderItems?.length && !selectedItems[0]?.thumbnail) return null

  return (
    <HorizontalScrollArea
      showControls
      controlsScrollType={HorizontalScrollArea.ControlScrollType.Partial}
      arrowLeftText={translateA11y('actions.move_left')}
      arrowRightText={translateA11y('actions.move_right')}
    >
      {renderItems()}
      <HorizontalScrollArea.Item key="add-more-items">
        {renderAddButton()}
      </HorizontalScrollArea.Item>
    </HorizontalScrollArea>
  )
}

export default ItemSelection
